import { useEffect, useState } from "react";
import { isCancelledDeal, isOpenDeal } from "../../../../types/OrderFormTypes";
import { Col, Layout, Row, Space, Spin } from "antd";
import { clsx } from "clsx";
import { Tabs } from "../../../../design-system";
import { PeriodSelector } from "./components/PeriodSelector";
import { useUserState } from "../../../../common/UserContext";
import { handleError } from "../../../../common/ErrorHandling";
import { getDashboardData } from "../../../../common/api/DashboardClientV2";
import { DashboardData } from "../../../../types/DashboardTypes";
import { Banner } from "./components/Banner";
import { DealsTable } from "./components/DealsTable";
import { RfxTable } from "./components/RfxTable";
import classes from "./NegotiationsView.module.less";

const getQuarter = (date: Date) => Math.ceil((new Date(date).getMonth() + 1) / 3);
const getYear = (date: Date) => new Date(date).getFullYear();

function filterOrderForms(orderForms: DashboardData["orderForms"], selectedYear: number | null) {
    const forms =
        selectedYear === null
            ? orderForms
            : orderForms.filter(
                  (o) => o.orderEndDate && getYear(new Date(o.orderEndDate)) === selectedYear
              );
    return [...forms].sort(
        (a, b) => Number(b.targetedSavings || 0) - Number(a.targetedSavings || 0)
    );
}

function filterTactics(tactics: DashboardData["tactics"], selectedYear: number | null) {
    const filtered =
        selectedYear === null
            ? tactics
            : tactics.filter(
                  (t) =>
                      t.recordCreationDate &&
                      getYear(new Date(t.recordCreationDate)) === selectedYear
              );
    return [...filtered].sort(
        (a, b) =>
            new Date(a.recordCreationDate).getTime() - new Date(b.recordCreationDate).getTime()
    );
}

export const NegotiationsView = () => {
    const { me } = useUserState();
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [selectedQuarters, setSelectedQuarters] = useState<number[]>([1]);
    const [checkedQuarters, setCheckedQuarters] = useState<number[]>([]);
    const emptyDashboard: DashboardData = {
        orderForms: [],
        tactics: [],
        metrics: [],
        intelligence: {
            totalSpend: 0,
            orderCount: 0,
            rfxCount: 0,
            avgOrderScore: 0,
            avgOptimizedScore: 0,
            totalSavings: 0,
            totalAvgScore: 0,
        },
        yearly: {},
    };
    const [dashboard, setDashboard] = useState<DashboardData>(emptyDashboard);
    const [listTab, setListTab] = useState<"Deals" | "RFXs">("Deals");
    const [loading, setLoading] = useState(false);

    const ALL_YEARS = -1;
    const yearOptions = [
        { label: "All", value: ALL_YEARS },
        ...Object.keys(dashboard.yearly)
            .map(Number)
            .sort((a, b) => a - b)
            .map((y) => ({ label: String(y), value: y })),
    ];

    const yearData = selectedYear !== null ? dashboard.yearly[String(selectedYear)] : null;

    const filteredOrderForms = filterOrderForms(dashboard.orderForms, selectedYear);

    const filteredTactics = filterTactics(dashboard.tactics, selectedYear);

    const filteredIntelligence = yearData?.intelligence ?? dashboard.intelligence;

    const quarterOptions = yearData?.quarters ?? dashboard.metrics;

    const tacticQuarterOptions = quarterOptions.map((m) => ({
        quarter: m.quarter,
        contracts: m.tactics,
        savings: 0,
    }));

    // The banner tally comes from the same list the cards below render, so the
    // two always agree. The server's quarter metrics can't include deals with no
    // end date (they belong to no quarter), so they are only used for the
    // quarter cards.
    // A cancelled deal's savings were only identified: they count in neither sum.
    // Realized means banked: what open deals have realized so far stays out.
    const pursuedOrderForms = filteredOrderForms.filter((o) => !isCancelledDeal(o));
    const yearMetric = {
        contracts: filteredOrderForms.length,
        savings: pursuedOrderForms.reduce(
            (sum, o) => sum + Number(o.targetedSavings || 0) / 100,
            0
        ),
        realized: pursuedOrderForms
            .filter((o) => !isOpenDeal(o))
            .reduce((sum, o) => sum + Number(o.realizedSavings || 0) / 100, 0),
    };

    // When "All" is selected, show all filtered data; when a year is selected, also filter by quarter
    const displayedOrders =
        selectedYear === null
            ? filteredOrderForms
            : filteredOrderForms.filter((o) =>
                  selectedQuarters.includes(getQuarter(new Date(o.orderEndDate!)))
              );
    const displayedTactics =
        selectedYear === null
            ? filteredTactics
            : filteredTactics.filter((t) =>
                  selectedQuarters.includes(getQuarter(new Date(t.recordCreationDate)))
              );

    const handleQuarterSelect = (quarter: number) => {
        setSelectedQuarters([quarter]);
        setCheckedQuarters([]);
    };

    const handleQuarterToggle = (quarter: number) => {
        setCheckedQuarters((prev) => {
            // If no checkboxes yet, seed from current selection
            const base = prev.length === 0 ? [...selectedQuarters] : prev;
            const next = base.includes(quarter)
                ? base.filter((q) => q !== quarter)
                : [...base, quarter].sort();
            setSelectedQuarters(next.length > 0 ? next : [quarter]);
            return next;
        });
    };

    const handleYearChange = (year: number) => {
        setSelectedYear(year === ALL_YEARS ? null : year);
        setSelectedQuarters([1]);
        setCheckedQuarters([]);
    };

    // Fetched once on mount; a row edit (an owner reassigned) refetches in
    // place, without the spinner, so the table does not flash.
    const load = (showSpinner: boolean) => {
        if (showSpinner) setLoading(true);
        getDashboardData()
            .then((data) => {
                setDashboard(data);
            })
            .catch(handleError)
            .finally(() => {
                if (showSpinner) setLoading(false);
            });
    };
    useEffect(() => {
        load(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Deals or RFXs: a view switch, first in the table's toolbar.
    const listSwitch = (
        <Tabs
            tabBarStyle={{ marginBottom: 0 }}
            items={[
                { key: "Deals", label: "Deals" },
                { key: "RFXs", label: "RFXs" },
            ]}
            activeKey={listTab}
            onChange={(key) => setListTab(key as "Deals" | "RFXs")}
        />
    );

    return (
        <Layout className={classes.layout}>
            <Layout.Header className={classes.header}>
                <h2 style={{ margin: 0 }}>Top Opportunities</h2>
                <p style={{ margin: 0, color: "rgba(0, 0, 0, 0.45)" }}>
                    Unlock smarter deal making with data-driven negotiation intelligence.
                </p>
            </Layout.Header>

            <Layout.Content className={classes.content}>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Banner
                        name={me.firstName}
                        savings={yearMetric.savings}
                        realized={yearMetric.realized}
                        contracts={yearMetric.contracts}
                        year={selectedYear}
                        intelligence={filteredIntelligence}
                    />
                    <Row>
                        <Col>
                            <h3 style={{ margin: 0 }}>Top savings opportunities</h3>
                            <p style={{ margin: 0, color: "rgba(0, 0, 0, 0.45)" }}>
                                View target savings by quarter and year.
                            </p>
                        </Col>
                    </Row>
                    <Row gutter={24} wrap={false}>
                        {/* The quarter cards keep their width; the list takes the rest. */}
                        <Col flex="200px">
                            <PeriodSelector
                                year={selectedYear ?? ALL_YEARS}
                                yearOptions={yearOptions}
                                onYearChange={handleYearChange}
                                selectedQuarters={selectedQuarters}
                                checkedQuarters={checkedQuarters}
                                hideQuarters={selectedYear === null}
                                quarterOptions={
                                    listTab === "Deals"
                                        ? quarterOptions
                                        : tacticQuarterOptions.map((o) => ({
                                              ...o,
                                              contractsLabel: "RFx created",
                                              type: "rfx" as const,
                                          }))
                                }
                                onQuarterSelect={handleQuarterSelect}
                                onQuarterToggle={handleQuarterToggle}
                            />
                        </Col>
                        <Col flex="auto" className={classes.list}>
                            {loading ? (
                                <div style={{ textAlign: "center", padding: 48 }}>
                                    <Spin size="large" />
                                </div>
                            ) : (
                                // Both tables stay mounted and the switch only
                                // hides one: remounting a hundred deal rows on
                                // every switch was a visible stall.
                                <>
                                    <div className={clsx(listTab !== "Deals" && classes.inactive)}>
                                        <DealsTable
                                            deals={displayedOrders}
                                            controls={listSwitch}
                                            onUpdate={() => load(false)}
                                            emptyText="No deals for this period"
                                        />
                                    </div>
                                    <div className={clsx(listTab !== "RFXs" && classes.inactive)}>
                                        <RfxTable
                                            tactics={displayedTactics}
                                            controls={listSwitch}
                                            emptyText="No RFx for this period"
                                        />
                                    </div>
                                </>
                            )}
                        </Col>
                    </Row>
                </Space>
            </Layout.Content>
        </Layout>
    );
};
